import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { dungPhien } from '../auth/PhienDangNhap';
import { DaiKetNoiTrung } from '../components/DaiKetNoiTrung';
import { DaiBanMoi } from '../components/DaiBanMoi';
import { ChuongThongBao } from '../components/ChuongThongBao';
import { MAN_HINH, NHOM_MENU, TAB_DIEN_THOAI, coManHinh, laDuongDangMo, timManHinh, type ManHinh, type MucConMenu } from '../spec/man-hinh';
import { DUONG_TAI_LIEU } from '../spec/tai-lieu';

function BieuTuong({ d, cao = 19 }: { d: string; cao?: number }) {
  return (
    <svg
      width={cao}
      height={cao}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

/** Biểu tượng quyển sách mở cho nút tài liệu hướng dẫn. */
const D_TAI_LIEU = 'M4 5.5A1.5 1.5 0 015.5 4H11v16H5.5A1.5 1.5 0 014 18.5zM20 5.5A1.5 1.5 0 0018.5 4H13v16h5.5a1.5 1.5 0 001.5-1.5zM7 8h1.5M15.5 8H17';

/** Biểu tượng ba gạch cho nút "Thêm". */
const D_THEM = 'M4 7h16M4 12h16M4 17h16';

/** Mũi tên lên cho nút cuộn về đầu. */
const D_LEN = 'M12 19V5M5 12l7-7 7 7';

/** Cuộn quá bao nhiêu điểm ảnh thì mới hiện nút về đầu. */
const NGUONG_HIEN_NUT_LEN = 240;

/** Mũi tên đôi: thu menu (sang trái) / mở menu (sang phải) — BOSS 07/10/2026. */
const D_THU_MENU = 'M11 17l-5-5 5-5M18 17l-5-5 5-5';
const D_MO_MENU = 'M13 17l5-5-5-5M6 17l5-5-5-5';
/** Mũi tên xuống / lên cho tay nắm thanh tab điện thoại. */
const D_XUONG = 'M6 9l6 6 6-6';
const D_LEN_NHO = 'M6 15l6-6 6 6';

/**
 * Lựa chọn thu / mở menu được NHỚ theo trình duyệt — chỉ là tiện ích hiển thị:
 * trình duyệt chặn lưu thì vẫn chạy, chỉ là lần sau quay về mặc định.
 */
const KHOA_THU_MENU = 'alads.menu.thu-gon';
const KHOA_AN_TAB = 'alads.menu.an-tab-dien-thoai';
function docNho(khoa: string): boolean | null {
  try {
    const v = localStorage.getItem(khoa);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}
function ghiNho(khoa: string, v: boolean): void {
  try {
    localStorage.setItem(khoa, v ? '1' : '0');
  } catch {
    /* trình duyệt chặn lưu — không sao */
  }
}

/** Màn 760–1119px: mặc định thu gọn menu (như trước), trừ khi người dùng đã chọn. */
const MQ_HEP = '(max-width: 1119px)';
function dungManHep(): boolean {
  const [hep, setHep] = useState(() => window.matchMedia?.(MQ_HEP).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(MQ_HEP);
    if (!mq) return;
    const nghe = () => setHep(mq.matches);
    mq.addEventListener('change', nghe);
    return () => mq.removeEventListener('change', nghe);
  }, []);
  return hep;
}

function doiNhomTra(s: Set<string>, id: string): Set<string> {
  const moi = new Set(s);
  moi.delete(id);
  return moi;
}

export function KhungChinh() {
  const { nguoiDung, co, dangXuat, congTy, doiCongTy } = dungPhien();
  const viTri = useLocation();
  const dieuHuong = useNavigate();
  const [moNganKeo, setMoNganKeo] = useState(false);

  // Thu / mở menu trái (máy tính) và ẩn / hiện thanh tab (điện thoại) — BOSS 07/10/2026.
  // `null` = chưa chọn → theo bề rộng màn hình như trước.
  const manHep = dungManHep();
  const [chonThu, setChonThu] = useState<boolean | null>(() => docNho(KHOA_THU_MENU));
  const thuMenu = chonThu ?? manHep;
  const doiThuMenu = () => {
    setChonThu(!thuMenu);
    ghiNho(KHOA_THU_MENU, !thuMenu);
  };
  const [anTab, setAnTab] = useState<boolean>(() => docNho(KHOA_AN_TAB) === true);
  const doiAnTab = () => {
    setAnTab(!anTab);
    ghiNho(KHOA_AN_TAB, !anTab);
  };

  // Menu 9 mục chính cố định + menu con (BOSS 19/10/2026). Quyền vẫn theo nhóm `nav`: mục chính hiện khi
  // được xem màn của nó HOẶC ít nhất một mục con; không được xem màn chính thì bấm vào mở mục con đầu.
  const duocXem = (id: string) => coManHinh(id, co, nguoiDung?.roleId);
  const menu = NHOM_MENU.map((g) => ({ man: timManHinh(g.id), xemChinh: duocXem(g.id), con: g.con.filter((c) => duocXem(c.manHinh)) }))
    .filter((g): g is { man: ManHinh; xemChinh: boolean; con: MucConMenu[] } => Boolean(g.man) && (g.xemChinh || g.con.length > 0));
  const conDangMo = (c: MucConMenu) => laDuongDangMo(c.duong, viTri.pathname, viTri.search);
  const nhomChua = menu.find((g) => g.con.some(conDangMo))?.man.id ?? null;
  const [moNhom, setMoNhom] = useState<Set<string>>(() => new Set(nhomChua ? [nhomChua] : []));
  // Đi tới một mục con (từ link trong trang, nút quay lại...) → tự sổ nhóm chứa nó.
  useEffect(() => {
    if (nhomChua) setMoNhom((s) => (s.has(nhomChua) ? s : new Set(s).add(nhomChua)));
  }, [nhomChua]);
  const doiNhom = (id: string) =>
    setMoNhom((s) => {
      const moi = new Set(s);
      if (moi.has(id)) moi.delete(id);
      else moi.add(id);
      return moi;
    });
  const manHienTai =
    MAN_HINH.find((m) => m.duong === viTri.pathname) ?? timManHinh('dashboard');

  /**
   * Vùng cuộn của ứng dụng là `<main className="ag-noi-dung">`, không phải cửa
   * sổ trình duyệt — cột danh mục và thanh tiêu đề đứng yên. Nên nút "về đầu"
   * phải nghe và cuộn chính phần tử này, `window.scrollTo` sẽ không có tác dụng.
   */
  const vungCuon = useRef<HTMLElement>(null);
  const [hienNutLen, setHienNutLen] = useState(false);

  useEffect(() => {
    const o = vungCuon.current;
    if (!o) return;
    const nghe = () => setHienNutLen(o.scrollTop > NGUONG_HIEN_NUT_LEN);
    nghe(); // tính ngay lần đầu, phòng khi vào trang đã ở giữa chừng
    o.addEventListener('scroll', nghe, { passive: true });
    return () => o.removeEventListener('scroll', nghe);
  }, []);

  // Đổi màn thì đưa về đầu trang. Trước đây trình duyệt tự lo việc này, nhưng
  // giờ phần cuộn nằm trong một phần tử nên phải tự đặt lại.
  useEffect(() => {
    vungCuon.current?.scrollTo({ top: 0 });
  }, [viTri.pathname]);

  const veDau = useCallback(() => {
    // Ai đã bật "giảm chuyển động" trong hệ điều hành thì nhảy thẳng, không trượt.
    const nhe = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    vungCuon.current?.scrollTo({ top: 0, behavior: nhe ? 'auto' : 'smooth' });
  }, []);

  // Đổi màn thì đóng ngăn kéo, và chặn cuộn nền khi ngăn kéo đang mở.
  useEffect(() => setMoNganKeo(false), [viTri.pathname, viTri.search]);
  useEffect(() => {
    // Khoá đúng vùng đang cuộn. Trước đây khoá `document.body`, nhưng từ khi
    // phần cuộn dồn vào `.ag-noi-dung` thì khoá body không còn tác dụng gì.
    const o = vungCuon.current;
    if (!o) return;
    o.style.overflowY = moNganKeo ? 'hidden' : '';
    return () => {
      o.style.overflowY = '';
    };
  }, [moNganKeo]);

  // Đóng bằng phím Esc — bàn phím ngoài trên máy tính bảng hay dùng.
  useEffect(() => {
    if (!moNganKeo) return;
    const nghe = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoNganKeo(false);
    };
    window.addEventListener('keydown', nghe);
    return () => window.removeEventListener('keydown', nghe);
  }, [moNganKeo]);

  /**
   * Ba tab cố định ở thanh dưới. Những màn còn lại nằm trong ngăn kéo "Thêm" —
   * nếu không có nút này thì trên điện thoại chỉ vào được 3 trong 15 màn.
   */
  const tabCoDinh = TAB_DIEN_THOAI.filter((t) => co('nav', t.id));
  // Ngăn kéo "Thêm": đủ 9 mục chính + mục con, cùng thứ tự với menu trái (BOSS 19/10/2026), cuối là Hồ sơ.
  const hoSo = MAN_HINH.find((m) => m.id === 'profile');

  return (
    <div className={`ag-shell${anTab ? ' ag-shell--an-tab' : ''}`}>
      <nav className={`ag-sidebar${thuMenu ? ' ag-sidebar--thu' : ''}`} aria-label="Menu chính">
        <div className="ag-sidebar__thuong-hieu">
          <img src="/logo-gldtech.png" alt="" width={28} height={28} />
          <span>ALADS-GLDtech</span>
        </div>
        {menu.map(({ man: m, xemChinh, con }) => {
          const mo = moNhom.has(m.id) && !thuMenu;
          const dich = xemChinh ? m.duong : con[0]!.duong;
          const chinhDangMo = xemChinh && viTri.pathname === m.duong && !con.some(conDangMo);
          return (
            <div key={m.id} className="ag-nav-nhom">
              <div className="ag-nav-nhom__dau">
                <NavLink
                  to={dich}
                  end
                  onClick={() => {
                    // Bấm mục chính: mở màn của nó + sổ menu con; đang đứng ở chính màn đó thì bấm lại để thu.
                    if (con.length) setMoNhom((s) => (viTri.pathname === m.duong && s.has(m.id) ? (doiNhomTra(s, m.id)) : new Set(s).add(m.id)));
                  }}
                  className={() => `ag-nav-muc${chinhDangMo ? ' dang-mo' : ''}${con.some(conDangMo) ? ' co-con-mo' : ''}`}
                  title={m.label}
                  aria-expanded={con.length ? mo : undefined}
                >
                  <BieuTuong d={m.d} />
                  <span>{m.label}</span>
                </NavLink>
                {con.length > 0 && !thuMenu && (
                  <button
                    type="button"
                    className="ag-nav-nhom__mui"
                    onClick={() => doiNhom(m.id)}
                    aria-label={mo ? `Thu menu ${m.label}` : `Mở menu ${m.label}`}
                    title={mo ? 'Thu menu con' : 'Mở menu con'}
                  >
                    <BieuTuong d={mo ? D_LEN_NHO : D_XUONG} cao={14} />
                  </button>
                )}
              </div>
              {mo && (
                <div className="ag-nav-con" role="group" aria-label={m.label}>
                  {con.map((c) => (
                    <NavLink key={c.duong} to={c.duong} className={() => `ag-nav-con__muc${conDangMo(c) ? ' dang-mo' : ''}`} title={c.nhan}>
                      {c.nhan}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className="ag-nav-muc ag-sidebar__thu-phong"
          onClick={doiThuMenu}
          aria-expanded={!thuMenu}
          aria-label={thuMenu ? 'Mở rộng menu' : 'Thu gọn menu'}
          title={thuMenu ? 'Mở rộng menu' : 'Thu gọn menu'}
        >
          <BieuTuong d={thuMenu ? D_MO_MENU : D_THU_MENU} />
          <span>Thu gọn menu</span>
        </button>
      </nav>

      <div className="ag-than">
        <header className="ag-topbar">
          <h1 className="ag-topbar__tieu-de">{manHienTai?.label ?? 'ALADS'}</h1>
          <div className="ag-topbar__phai">
            <ChonCongTy congTy={congTy} doiCongTy={doiCongTy} />
            {/* Tài liệu hướng dẫn (BOSS 20/10/2026): ai đăng nhập cũng thấy, không theo quyền. */}
            <a
              href={DUONG_TAI_LIEU}
              target="_blank"
              rel="noopener"
              aria-label="Tài liệu hướng dẫn sử dụng ALADS (PDF)"
              title="Tài liệu hướng dẫn sử dụng ALADS (PDF)"
              className="ag-nut-tai-lieu"
              style={{
                width: 36,
                height: 36,
                flex: 'none',
                borderRadius: 10,
                border: '1px solid var(--alads-vien-2)',
                background: 'var(--alads-nen-trang)',
                color: 'var(--alads-muc-2)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <BieuTuong d={D_TAI_LIEU} />
            </a>
            <ChuongThongBao />
            <div className="ag-topbar__ten" style={{ textAlign: 'right', lineHeight: 1.3 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{nguoiDung?.name}</div>
              <div className="ag-mo" style={{ fontSize: 11 }}>
                {nguoiDung?.roleLabel}
              </div>
            </div>
            <NavLink to="/ho-so" className="ag-chu-cai" title="Hồ sơ tài khoản">
              {nguoiDung?.name
                ?.split(' ')
                .filter(Boolean)
                .map((p) => p[0])
                .slice(-2)
                .join('') ?? '··'}
            </NavLink>
            <button
              type="button"
              onClick={dangXuat}
              style={{
                border: '1px solid var(--alads-vien-2)',
                background: 'var(--alads-nen-trang)',
                color: 'var(--alads-muc-2)',
                borderRadius: 'var(--alads-bo-goc-nho)',
                padding: '8px 12px',
                fontSize: 12.5,
                cursor: 'pointer',
              }}
            >
              Đăng xuất
            </button>
          </div>
        </header>

        <main className="ag-noi-dung" ref={vungCuon}>
          <DaiBanMoi />
          <DaiKetNoiTrung />
          <Outlet />
        </main>

        <button
          type="button"
          className={`ag-len-dau${hienNutLen ? ' hien' : ''}`}
          onClick={veDau}
          aria-label="Cuộn về đầu trang"
          title="Về đầu trang"
          /* Ẩn khỏi thứ tự Tab khi đang không hiện, để bàn phím không lạc vào
             một nút vô hình. */
          tabIndex={hienNutLen ? 0 : -1}
          aria-hidden={!hienNutLen}
        >
          <BieuTuong d={D_LEN} cao={18} />
        </button>
      </div>

      {/* --- Ngăn kéo "Thêm" trên điện thoại --- */}
      {moNganKeo && (
        <>
          <button
            type="button"
            className="ag-ngan-keo-nen"
            aria-label="Đóng menu"
            onClick={() => setMoNganKeo(false)}
          />
          <div className="ag-ngan-keo" role="dialog" aria-label="Menu đầy đủ">
            <div className="ag-ngan-keo__tay-cam" />
            {menu.map(({ man: m, xemChinh, con }) => (
              <div key={m.id}>
                {xemChinh && (
                  <button
                    type="button"
                    className={`ag-ngan-keo__muc${m.duong === viTri.pathname && !con.some(conDangMo) ? ' dang-mo' : ''}`}
                    onClick={() => dieuHuong(m.duong)}
                  >
                    <BieuTuong d={m.d} cao={20} />
                    {m.label}
                  </button>
                )}
                {!xemChinh && (
                  <div className="ag-ngan-keo__muc" style={{ cursor: 'default', opacity: 0.8 }}>
                    <BieuTuong d={m.d} cao={20} />
                    {m.label}
                  </div>
                )}
                {con.map((c) => (
                  <button
                    key={c.duong}
                    type="button"
                    className={`ag-ngan-keo__muc ag-ngan-keo__muc--con${conDangMo(c) ? ' dang-mo' : ''}`}
                    onClick={() => dieuHuong(c.duong)}
                  >
                    {c.nhan}
                  </button>
                ))}
              </div>
            ))}
            {hoSo && (
              <button
                type="button"
                className={`ag-ngan-keo__muc${hoSo.duong === viTri.pathname ? ' dang-mo' : ''}`}
                onClick={() => dieuHuong(hoSo.duong)}
              >
                <BieuTuong d={hoSo.d} cao={20} />
                {hoSo.label}
              </button>
            )}
            {/* Thanh trên cùng điện thoại quá chật nên nút tài liệu nằm ở đây thay vì cạnh chuông. */}
            <a
              href={DUONG_TAI_LIEU}
              target="_blank"
              rel="noopener"
              className="ag-ngan-keo__muc"
              style={{ textDecoration: 'none', color: 'inherit' }}
              onClick={() => setMoNganKeo(false)}
            >
              <BieuTuong d={D_TAI_LIEU} cao={20} />
              Tài liệu hướng dẫn (PDF)
            </a>
            <button
              type="button"
              className="ag-ngan-keo__muc"
              onClick={dangXuat}
              style={{ color: 'var(--alads-do)', marginTop: 6 }}
            >
              <BieuTuong d="M15 12H3m0 0l4-4m-4 4l4 4M13 4h5a2 2 0 012 2v12a2 2 0 01-2 2h-5" cao={20} />
              Đăng xuất
            </button>
          </div>
        </>
      )}

      <nav className={`ag-tabbar${anTab ? ' ag-tabbar--an' : ''}`} aria-label="Menu điện thoại">
        {/* Tay nắm mũi tên: ▼ thu thanh tab xuống, ▲ mở lên (BOSS 07/10/2026). Nằm nhô
            trên mép thanh nên khi thanh trượt xuống vẫn còn thấy để bấm mở lại. */}
        <button
          type="button"
          className="ag-tabbar__keo"
          onClick={doiAnTab}
          aria-expanded={!anTab}
          aria-label={anTab ? 'Mở thanh menu' : 'Thu thanh menu xuống'}
          title={anTab ? 'Mở thanh menu' : 'Thu thanh menu xuống'}
        >
          <BieuTuong d={anTab ? D_LEN_NHO : D_XUONG} cao={16} />
        </button>
        {tabCoDinh.map((t) => {
          const man = timManHinh(t.id);
          if (!man) return null;
          return (
            <NavLink
              key={t.id}
              to={man.duong}
              end={man.duong === '/'}
              className={({ isActive }) =>
                `ag-tabbar__nut${isActive && !moNganKeo ? ' dang-mo' : ''}`
              }
            >
              <BieuTuong d={man.d} />
              {t.label}
            </NavLink>
          );
        })}

        <button
          type="button"
          className={`ag-tabbar__nut${moNganKeo ? ' dang-mo' : ''}`}
          onClick={() => setMoNganKeo((v) => !v)}
          aria-expanded={moNganKeo}
        >
          <BieuTuong d={D_THEM} />
          Thêm
        </button>
      </nav>
    </div>
  );
}

/**
 * Thanh chọn công ty (BOSS 29/09/2026 — nhiều công ty trong một ALADS). Chỉ hiện
 * khi có hơn một lựa chọn. "Tất cả công ty" chỉ dành cho Chủ DN / Administrator.
 * Đổi công ty = xem dữ liệu VÀ làm việc với vai trò tại công ty đó.
 */
function ChonCongTy({
  congTy,
  doiCongTy,
}: {
  congTy: ReturnType<typeof dungPhien>['congTy'];
  doiCongTy: (id: string | null) => void;
}) {
  const soLuaChon = congTy.danhSach.length + (congTy.duocXemTatCa ? 1 : 0);
  if (soLuaChon <= 1) {
    const mot = congTy.danhSach[0];
    return mot ? (
      <span className="ag-mo" title="Công ty đang làm việc" style={{ fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {mot.ten}
      </span>
    ) : null;
  }
  return (
    <label className="ag-chon-cong-ty" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, minWidth: 0 }} title="Công ty đang làm việc">
      <span className="ag-mo ag-chon-cong-ty__nhan">Công ty</span>
      <select
        value={congTy.hienTai ?? ''}
        onChange={(e) => doiCongTy(e.target.value || null)}
        aria-label="Chọn công ty"
        style={{
          font: 'inherit',
          fontSize: 12.5,
          maxWidth: 240,
          padding: '6px 8px',
          borderRadius: 'var(--alads-bo-goc-nho)',
          border: '1px solid var(--alads-vien-2)',
          background: 'var(--alads-nen-trang)',
          color: 'var(--alads-muc)',
        }}
      >
        {congTy.duocXemTatCa && <option value="tat-ca">Tất cả công ty</option>}
        {congTy.danhSach.map((c) => (
          <option key={c.id} value={c.id}>
            {c.ten}
          </option>
        ))}
      </select>
    </label>
  );
}
